import type { PageName } from '@ms/yammer-telemetry-support';

import { startPageLoadScenario } from './startPageLoadScenario';
import type { PageLoadScenarioHandle, StartPageLoadScenarioOptions } from './startPageLoadScenario';

/**
 * The static config for a page-load scenario: which navigation scenario a page represents and the
 * entry source it is attributed to. `timeoutInMS`/`reportScenarioSettled` are supplied by the caller at start
 * time, not baked into the registry.
 */
export type PageLoadScenarioRegistryEntry = Pick<StartPageLoadScenarioOptions, 'name' | 'source'>;

/**
 * A map from route `PageName` (the stable per-route identifier the page-load telemetry already
 * carries) to the page-load scenario it represents. This package owns the *mechanism* for turning
 * such a map into scenarios, but not the map itself: the concrete registry is app-provided, because
 * which pages exist and how they group onto scenarios is app routing domain knowledge (yammer-web,
 * teams, and sharepoint each have their own routes). A feature crew onboards by adding one entry to
 * their app's registry; several page names may map onto one scenario (e.g. a community's feed tabs
 * all represent `open_community_feed`).
 */
export type PageLoadScenarioRegistry = Partial<Record<PageName, PageLoadScenarioRegistryEntry>>;

/**
 * Starts the page-load scenario `registry` associates with `page`, or returns `undefined` if the
 * page is not registered (or `page` is absent). The caller supplies the runtime-only options
 * (`timeoutInMS`/`reportScenarioSettled`); the scenario name and source come from the registry entry. This is
 * the single entry point the app-level bridge calls on navigation, keeping the scenario-start
 * mechanism inside this package while the map stays app-owned.
 */
export const startPageLoadScenarioForPage = (
  registry: PageLoadScenarioRegistry,
  page: PageName | undefined,
  options?: Pick<StartPageLoadScenarioOptions, 'timeoutInMS' | 'reportScenarioSettled'>
): PageLoadScenarioHandle | undefined => {
  const entry = page ? registry[page] : undefined;

  return entry ? startPageLoadScenario({ ...entry, ...options }) : undefined;
};
